import { useForm } from "@tanstack/react-form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { DEFAULT_VERTICAL, verticalsQuery } from "@/api/verticals";
import { AuthError, useAuth } from "@/auth/AuthProvider";
import { attemptGoogleSignIn } from "@/auth/google-signin";
import { finishOnboarding, startOnboarding } from "@/auth/onboarding";
import { GoogleButton } from "@/components/google-button";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
  FieldTitle,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AccountValues,
  accountSchema,
  type BusinessValues,
  businessSchema,
  detectTimezone,
} from "@/lib/schemas";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_public/cadastro")({
  // Preenchido quando o usuário veio do Google e a conta ainda não existe: o
  // ID token viaja até aqui para completarmos o cadastro numa só etapa.
  // Devolver `{}` quando ausente mantém o parâmetro opcional nos Links.
  validateSearch: (search: Record<string, unknown>): { google?: string } =>
    typeof search.google === "string" ? { google: search.google } : {},
  component: SignupPage,
});

type Step = "account" | "business";

function SignupPage() {
  const { signup, loginWithGoogle } = useAuth();
  const router = useRouter();
  const { google } = Route.useSearch();
  const [formError, setFormError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("account");
  // Guardado aqui, e não submetido ainda: o `POST /auth/signup` cria conta e
  // estabelecimento na mesma transação, então só dá pra chamar no fim da
  // etapa 2. Manter os valores também é o que faz o "Voltar" não apagar nada.
  const [account, setAccount] = useState<AccountValues | null>(null);

  // No fluxo do Google a identidade já veio do provedor: a etapa da conta não
  // existe, e a tela abre direto no negócio.
  const isGoogleFlow = Boolean(google);
  const currentStep: Step = isGoogleFlow ? "business" : step;

  async function handleGoogle(idToken: string) {
    setFormError(null);
    const outcome = await attemptGoogleSignIn(loginWithGoogle, idToken);
    if (outcome.kind === "signed-in") {
      // Já existia conta com esse Google: entra direto, sem criar nada.
      await router.navigate({ to: "/" });
    } else if (outcome.kind === "needs-establishment") {
      // Conta nova: reaproveita esta tela, agora só com o que falta.
      await router.navigate({
        to: "/cadastro",
        search: { google: outcome.idToken },
      });
    } else {
      setFormError(outcome.message);
    }
  }

  async function completeSignup(business: BusinessValues) {
    setFormError(null);
    // Marcado ANTES de criar a conta: no instante em que o signup autentica a
    // sessão, o `beforeLoad` do `_public` dispara sozinho e redireciona — sem
    // esta marca ele mandaria a conta nova pro painel, pulando a persona.
    startOnboarding(business.vertical);
    try {
      if (google) {
        await loginWithGoogle(google, {
          establishmentName: business.establishment_name,
          timezone: detectTimezone(),
          vertical: business.vertical,
        });
      } else if (account) {
        await signup({
          ...account,
          establishmentName: business.establishment_name,
          timezone: detectTimezone(),
          vertical: business.vertical,
        });
      } else {
        // Sem os dados da etapa 1 não há o que enviar. Só acontece se o
        // estado for perdido no meio do caminho — volta em vez de falhar.
        finishOnboarding();
        setStep("account");
        return;
      }
      await router.navigate({ to: "/bem-vindo" });
    } catch (cause) {
      // A conta não nasceu: não há onboarding nenhum pendente.
      finishOnboarding();
      setFormError(describeSignupError(cause));
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {currentStep === "account" ? "Criar conta" : "Sobre o seu negócio"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {currentStep === "account"
            ? "Comece a receber agendamentos pelo WhatsApp."
            : "É com isso que o assistente fala com seus clientes."}
        </p>
      </div>

      {/* Quem entrou pelo Google só tem uma etapa — um indicador de progresso
          aqui contaria uma etapa que ele nunca vai ver. */}
      {!isGoogleFlow && <StepIndicator current={currentStep} />}

      {currentStep === "account" ? (
        <AccountStep
          defaultValues={account}
          error={formError}
          onDone={(values) => {
            setAccount(values);
            setFormError(null);
            setStep("business");
          }}
          onGoogle={(token) => void handleGoogle(token)}
        />
      ) : (
        <BusinessStep
          error={formError}
          isGoogleFlow={isGoogleFlow}
          onBack={() => {
            setFormError(null);
            setStep("account");
          }}
          onDone={completeSignup}
        />
      )}

      <FieldDescription className="text-center">
        Já tem conta?{" "}
        <Link
          to="/login"
          search={{ redirect: "/" }}
          className="underline underline-offset-4"
        >
          Entrar
        </Link>
      </FieldDescription>
    </div>
  );
}

function StepIndicator({ current }: { current: Step }) {
  const index = current === "account" ? 1 : 2;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1.5" aria-hidden="true">
        <span className="h-1 flex-1 rounded-full bg-primary" />
        <span
          className={cn(
            "h-1 flex-1 rounded-full",
            index === 2 ? "bg-primary" : "bg-muted",
          )}
        />
      </div>
      <p className="text-center text-xs text-muted-foreground">Etapa {index} de 2</p>
    </div>
  );
}

interface AccountStepProps {
  defaultValues: AccountValues | null;
  error: string | null;
  onDone: (values: AccountValues) => void;
  onGoogle: (idToken: string) => void;
}

function AccountStep({ defaultValues, error, onDone, onGoogle }: AccountStepProps) {
  const form = useForm({
    defaultValues: defaultValues ?? { name: "", email: "", password: "" },
    validators: { onSubmit: accountSchema },
    onSubmit: ({ value }) => onDone(value),
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup className="gap-4">
        <form.Field name="name">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Nome</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                autoComplete="name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="email">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>E-mail</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="email"
                autoComplete="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Senha</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="password"
                autoComplete="new-password"
                placeholder="Pelo menos 8 caracteres"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Field>
          <Button type="submit">Continuar</Button>
        </Field>

        <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
          ou
        </FieldSeparator>
        <GoogleButton onCredential={onGoogle} />
      </FieldGroup>
    </form>
  );
}

interface BusinessStepProps {
  error: string | null;
  isGoogleFlow: boolean;
  onBack: () => void;
  onDone: (values: BusinessValues) => Promise<void>;
}

function BusinessStep(props: BusinessStepProps) {
  return (
    <Suspense fallback={<BusinessStepSkeleton />}>
      <BusinessForm {...props} />
    </Suspense>
  );
}

function BusinessStepSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

function BusinessForm({ error, isGoogleFlow, onBack, onDone }: BusinessStepProps) {
  const { data: verticals } = useSuspenseQuery(verticalsQuery());

  const form = useForm({
    // Pré-seleciona o primeiro segmento que o servidor oferece (ele manda na
    // ordem de exibição). O fallback só existe pra lista vazia, que seria um
    // catálogo sem persona nenhuma — situação que o endpoint já evita.
    defaultValues: {
      establishment_name: "",
      vertical: verticals[0]?.key ?? DEFAULT_VERTICAL,
    },
    validators: { onSubmit: businessSchema },
    onSubmit: ({ value }) => onDone(value),
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup className="gap-4">
        <form.Field name="establishment_name">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>Nome do negócio</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                // Não é a empresa onde a pessoa trabalha, é o negócio que ela
                // está criando agora — "organization" só faria o gerenciador
                // despejar identidades do cofre num campo que ninguém preenche
                // com dado antigo.
                autoComplete="off"
                placeholder="Nome do seu estabelecimento"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldDescription>
                É o nome que seus clientes veem no WhatsApp.
              </FieldDescription>
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="vertical">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel>Segmento</FieldLabel>
              <RadioGroup
                className="gap-2"
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                {verticals.map((vertical) => (
                  <FieldLabel
                    key={vertical.key}
                    htmlFor={`vertical-${vertical.key}`}
                    className="[&>[data-slot=field]]:p-3"
                  >
                    <Field orientation="horizontal">
                      <FieldContent>
                        <FieldTitle>{vertical.label}</FieldTitle>
                        <FieldDescription>{vertical.description}</FieldDescription>
                      </FieldContent>
                      {/* O RadioGroupItem do Radix é um <button role="radio">,
                          e botão não é nomeado por <label for> — o nome
                          acessível tem que vir explícito. */}
                      <RadioGroupItem
                        id={`vertical-${vertical.key}`}
                        value={vertical.key}
                        aria-label={vertical.label}
                      />
                    </Field>
                  </FieldLabel>
                ))}
              </RadioGroup>
              <FieldDescription>
                Define como o assistente fala com seus clientes.
              </FieldDescription>
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Field orientation={isGoogleFlow ? "vertical" : "horizontal"}>
              {/* Quem veio do Google não tem etapa anterior pra onde voltar. */}
              {!isGoogleFlow && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Voltar
                </Button>
              )}
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? "Criando…" : "Criar conta"}
              </Button>
            </Field>
          )}
        </form.Subscribe>
      </FieldGroup>
    </form>
  );
}

function describeSignupError(cause: unknown): string {
  if (!(cause instanceof AuthError)) return "Não foi possível criar a conta.";
  switch (cause.code) {
    case "network_error":
      return "Sem conexão com o servidor. Verifique sua internet.";
    case "email_already_registered":
      return "Esse e-mail já tem conta. Tente entrar.";
    case "invalid_password":
      return "Senha fora do formato aceito (8 a 72 bytes).";
    case "invalid_email":
      return "Informe um e-mail válido.";
    case "rate_limited":
      return "Muitas tentativas. Aguarde um instante e tente de novo.";
    default:
      return "Não foi possível criar a conta.";
  }
}
